import { Appear, Button, Buttons, Flex, Icon, Link, Skeleton, Text } from '@bitrise/bitkit';
import { BitriseApp, BitriseAppProjectType } from '@trace/types';
import React, { FunctionComponent } from 'react';
import useBitriseApps from './useBitriseApps';
import AppSwitcherListItem from './AppSwitcherListItem';
import './AppSwitcherList.css';

const fetchingApps: BitriseApp[] = Array.from({ length: 5 }).map((_, i) => ({
  avatar_url: '',
  is_disabled: false,
  is_public: false,
  owner: {
    account_type: '',
    name: '',
    slug: '',
  },
  project_type: ['ios', 'android'][Math.round(Math.random())] as BitriseAppProjectType,
  provider: '',
  repo_owner: '',
  repo_slug: '',
  repo_url: '',
  slug: `_fetching_app-${i}`,
  status: 0,
  title: '',
}));

const AppSwitcherList: FunctionComponent<{ search: string }> = ({ search = '' }) => {
  const {
    apps,
    refetch,
    fetchNextPage,
    isFetching,
    isError,
    isFetched,
    nextPage,
    prevAppsCount,
  } = useBitriseApps(search);

  if (isError) {
    return (
      <Flex alignChildrenVertical="middle" direction="vertical" grow>
        <Appear animation="Fade" textColor="red-3">
          <Flex alignChildren="middle" direction="vertical" height="100%">
            <Flex>
              <Flex alignChildren="middle" direction="vertical">
                <Icon margin="x1" name="BitbotFailed" size="3rem" />
              </Flex>
              <Text align="middle" ellipsis>
                Uh oh, there&apos;s been a problem.
              </Text>
              <Text align="middle">
                {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                <Link clickable onClick={refetch} underline>
                  Click here to retry
                </Link>
                .
              </Text>
            </Flex>
          </Flex>
        </Appear>
      </Flex>
    );
  }

  if (isFetched && !apps.length) {
    return (
      <Flex alignChildrenVertical="middle" direction="vertical" grow>
        <Appear animation="Fade" paddingVertical="x6">
          {search ? (
            <>
              <Text align="middle">
                It looks like there aren&apos;t any apps matching &quot;{search}&quot;.
              </Text>
              <Text align="middle">Try searching for something else.</Text>
            </>
          ) : (
            <>
              <Text align="middle">It looks like you don&apos;t have any apps.</Text>
              <Text align="middle">Go create one and come back here.</Text>
            </>
          )}
        </Appear>
      </Flex>
    );
  }

  return (
    <Skeleton active={isFetching}>
      <Flex direction="vertical" gap="x3" className="AppSwitcher__AppList" Component="ul">
        {(isFetching && !apps.length ? fetchingApps : apps).map((app, i) => (
          <Appear
            animation="FadeSlideUp"
            Component="li"
            delay={50 * (i - prevAppsCount)}
            key={app.slug}
          >
            <AppSwitcherListItem {...app} />
          </Appear>
        ))}
      </Flex>

      {nextPage && (
        <Buttons alignChildren="middle" margin="x8">
          <Button level="tertiary" size="medium" onClick={fetchNextPage}>
            <Icon name="ArrowDown" />
            <Text>Load more Apps</Text>
          </Button>
        </Buttons>
      )}
    </Skeleton>
  );
};

export default AppSwitcherList;
